<template>
  <div
    v-if="showData"
    id="profile-layout"
    :class="{
      'mobile-app-ios': isMobileApp && platformId === 3,
      'mobile-app': isMobileApp
    }"
  >
    <v-app class="white--background profile" :class="{ 'dialog-opened': dialog }">
      <v-navigation-drawer
        touchless
        permanent
        fixed
        app
        height="100vmax"
        class="profile-navigation-container"
      >
        <ProfileNavigation />
      </v-navigation-drawer>
      <v-navigation-drawer
        touchless
        permanent
        fixed
        app
        width="320"
        height="100vmax"
        right
        class="profile-section-container"
      >
        <NeedHelpFab show-need-help-fab />
        <ProfileSection />
      </v-navigation-drawer>
      <v-main
        class="profile-app-container mx-4 mx-xl-8 bottom-navigation-offset"
        :class="{
          'dialog-page': currentPath === 'profileDialog'
        }"
      >
        <slot />
      </v-main>
      <BottomNavigation />
      <DashboardDialogs v-if="$auth.isLoggedIn" />
      <AppLockOverlay v-if="$store.getters['appLock/isAppLockEnabled']" />
      <DeviceNotSecureOverlay />
    </v-app>
    <UnsupportedBrowserModal id="unsupported-browser-modal" />
    <UnavailableClinics />
  </div>
  <v-app v-else class="white--background">
    <Section container-class="container--md" is-full-height>
      <Loading />
    </Section>
  </v-app>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import checkUserVerification from '@/mixins/checkUserVerification';
import profileLayoutSetup from '@/mixins/profileLayoutSetup';
import platformId from '@/mixins/platformId';
import authUserWatcher from '@/mixins/authUserWatcher';
import dashboardActiveServices from '@/mixins/dashboardActiveServices';
import { Capacitor } from '@capacitor/core';
import { useCapacitorPrivacyScreen } from '@/composables/capacitor/useCapacitorPrivacyScreen.ts';
import AppLockOverlay from '@/feature/app-lock/components/AppLockOverlay.vue';
import DeviceNotSecureOverlay from '@/feature/app-lock/components/DeviceNotSecureOverlay.vue';

export default {
  name: 'ProfileLayout',
  components: {
    AppLockOverlay,
    DeviceNotSecureOverlay
  },
  mixins: [
    profileLayoutSetup,
    platformId,
    authUserWatcher,
    checkUserVerification,
    dashboardActiveServices
  ],
  data() {
    return {
      setup: false
    };
  },
  metaInfo: {
    script: [
      {
        src: import.meta.env.MODE === 'production' ? '/linkedin-tracking-code.js' : '',
        body: true
      }
    ]
  },
  computed: {
    ...mapGetters({
      dialog: 'profile/dashboard/dialog',
      showNeedHelp: 'general/showNeedHelp',
      initialLoading: 'dynamicForms/initialLoading',
      token: 'apiToken/token',
      loadingPartnerData: 'profile/dashboard/loadingPartnerData'
    }),
    hasSecondFactorChallenge() {
      return this.$store.getters['auth/hasSecondFactorChallenge'];
    },
    showData() {
      return this.setup && this.$auth.isLoggedIn && !this.loadingPartnerData;
    }
  },
  watch: {
    async $route(to, from) {
      if (!this.setup) {
        return;
      }
      await this.checkUserVerification(false);
      if (from.name !== to.name) {
        this.gtmPush({
          hrbName: this.partnerData?.name,
          siteSection: 'klientska_zona',
          user_type: this.$auth.user?.benefit?.active ? 'b2b' : 'b2c'
        });
      }
      if (this.$auth.user && to.name.split('___')[1] !== this.$auth.user.language) {
        await this.$router.push(
          this.$localePath(
            {
              name: to.name.split('___')[0],
              query: to.query,
              params: to.params,
              hash: to.hash
            },
            this.$auth.user.language
          )
        );
        return;
      }
      if ((!to.query.pd || !to.query.pd.length) && !this.dialog) {
        //because of back navigation in browser/app - reflect missing pd query
        await this.$store.dispatch('profile/dashboard/setDialog', false);
      }
    },
    async '$auth.isBusy'(newValue) {
      if (newValue === false && !this.setup) {
        await this.getUserAppData();
        await this.checkUserAllowed();
        this.setup = true;
        await this.profileLayoutSetupInit();
      }
    }
  },
  beforeCreate() {
    this.$store.commit('general/setSendAuthorizationHeader', true);

    this.$graphql.medicalProcedures.requestConfig.requestMiddleware = request =>
      this.graphqlRequestMiddleware(request);
    this.$graphql.appSettings.requestConfig.requestMiddleware = request =>
      this.graphqlRequestMiddleware(request);

    this.$graphql.medicalProcedures.requestConfig.responseMiddleware = response =>
      this.graphqlResponseMiddleware(response);
    this.$graphql.appSettings.requestConfig.responseMiddleware = response =>
      this.graphqlResponseMiddleware(response);
  },
  async created() {
    await this.setHeader('none');

    if (this.$auth.token) {
      if (!this.$auth.user) {
        await this.$auth.fetchUser();
      }

      if (this.$auth.isLoggedIn && !this.hasSecondFactorChallenge) {
        await this.$store.dispatch('profile/dashboard/setQuestions');
        await this.$store.dispatch('profile/dashboard/setAppointments');
      }

      if (!this.setup && !this.$store.state.auth.isBusy && this.$auth.isLoggedIn) {
        await this.getUserAppData();
        await this.checkUserAllowed();
        this.setup = true;
        await this.profileLayoutSetupInit();
      }
    }
  },
  async beforeMount() {
    if (!this.$auth.user) {
      const wu = await this.$auth.fetchUser();
      if (!wu) {
        await this.$router.push(
          this.$localePath({
            name: 'login',
            query: {
              sard: this.$buildSard({
                params: this.$route.params,
                query: this.$route.query,
                page: this.$route.name.split('___')[0]
              })
            }
          })
        );
      }
    }

    /* eslint-disable id-length */
    this.setWindowSize({ x: window.innerWidth, y: window.innerHeight });
    window.addEventListener(
      'resize',
      () => {
        this.setWindowSize({ x: window.innerWidth, y: window.innerHeight });
      },
      true
    );
    /* eslint-enable */

    this._handleMobileInputFocus = event => {
      const el = event.target;
      if (window.innerWidth <= 960 && el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    };
    document.addEventListener('focus', this._handleMobileInputFocus, true);

    // Unified language usage
    if (this.$auth.user && this.$auth.user?.language !== this.$i18n.locale) {
      await this.$router.replace(this.$switchLocalePath(this.$auth.user?.language));
    }

    if (this.$auth?.user?.verify?.phone) {
      this.$store.commit('general/setAuthRedirectParams', '');
    }

    // Set only unified language in store
    this.$store.commit('profile/accountSettings/setLanguage', this.$auth.user?.language);

    if (Capacitor.isNativePlatform() && import.meta.env.MODE === 'production') {
      const privacyScreenApi = useCapacitorPrivacyScreen();
      await privacyScreenApi.enable();
    }
  },
  beforeDestroy() {
    this.$store.commit('general/setShowNeedHelp', false);
    if (this._handleMobileInputFocus) {
      document.removeEventListener('focus', this._handleMobileInputFocus, true);
    }
  },
  methods: {
    ...mapActions({
      updateInitialLoading: 'dynamicForms/updateInitialLoading',
      setHeader: 'general/setHeader'
    }),
    ...mapMutations({
      setWindowSize: 'general/setWindowSize'
    })
  }
};
</script>

<style scoped lang="scss">
#profile-layout {
  max-width: 1440px;
  margin: 0 auto;

  ::v-deep .v-application--wrap {
    position: absolute;
    width: 100%;
  }
}

.v-main {
  padding-top: 0 !important;
  margin-bottom: 64px;

  @media #{map-get($display-breakpoints, 'lg-and-up')} and (min-height: 681px) {
    padding-left: 106px !important;
    padding-right: 0 !important;
  }

  @media #{map-get($display-breakpoints, 'xl-and-up')} {
    padding-left: 231px !important;
  }

  @media #{map-get($display-breakpoints, 'xxl-only')} {
    padding-right: 320px !important;
  }

  @media #{map-get($display-breakpoints, 'md-and-down')}, #{map-get($display-breakpoints, 'xl-and-down')} and (max-height: 680px) {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}

.profile-app-bar {
  border-bottom: $border-width solid $grey-lighten;
  background-color: $white !important;
  height: 56px;
}

.profile-navigation-container {
  display: none;
  width: 96px !important;
  max-height: 100% !important;
  left: initial;

  @media #{map-get($display-breakpoints, 'lg-and-up')} and (min-height: 681px), #{map-get($display-breakpoints, 'xxl-only')} {
    display: flex;
  }

  @media #{map-get($display-breakpoints, 'xl-and-up')} {
    width: 231px !important;
  }
}

.profile-section-container {
  display: none;
  max-height: 100% !important;
  transition: none;

  @media #{map-get($display-breakpoints, 'xxl-only')} {
    display: flex;
  }

  @media only screen and (min-width: 1440px) {
    left: initial;
    right: initial;
    transform: translateX(calc(231px + 889px)) !important;
  }
}

.profile-app-container.basic-cookies-shown {
  margin-bottom: 450px;
  @media #{map-get($display-breakpoints, 'md-and-down')}, #{map-get($display-breakpoints, 'xl-and-down')} and (max-height: 680px) {
    margin-bottom: 550px;
  }
}

::v-deep #cookies-consent .v-bottom-sheet.v-dialog {
  @media #{map-get($display-breakpoints, 'md-and-down')}, #{map-get($display-breakpoints, 'xl-and-down')} and (max-height: 680px) {
    margin-bottom: 100px;
  }
}
</style>
